import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import type { StorefrontContentBlock } from '@nucleus-commerce/api-client/admin';

import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';

import { blocksApi } from '../api/content-api';
import {
  toPreviewBody,
  type PreviewBannerItem,
  type PreviewBlockBody,
  type PreviewMediaRef,
} from '../api/mappers';
import type { BlockFormInput } from '../schemas/block-form-schema';

import { contentKeys } from './content-keys';

export const PREVIEW_DEBOUNCE_MS = 400;
const PREVIEW_STALE_MS = 30_000;

/** Alt text is copy: it changes nothing the resolver looks up, so it stays out of the key. */
function lookupOf(ref: PreviewMediaRef | null | undefined) {
  if (!ref) return null;
  const { alt: _alt, ...rest } = ref;
  return rest;
}

function itemLookups(item: PreviewBannerItem) {
  return {
    media: lookupOf(item.media),
    mobileMedia: lookupOf(item.mobileMedia),
    ctas: (item.ctas ?? []).map((cta) => cta.link),
  };
}

/**
 * The part of a preview body the server resolves: media refs, button links, product ids, the
 * collection, the limit and the kind. Copy (titles, labels, alt text, custom data) is left out,
 * so typing it never refetches; the panel renders copy straight from the form.
 */
export function resolvableKey(body: PreviewBlockBody): string {
  if (body.kind === 'banner') {
    return JSON.stringify({ kind: 'banner', items: body.banner.items.map(itemLookups) });
  }
  const { header, source, limit } = body.rail;
  return JSON.stringify({
    kind: 'rail',
    header: header ? itemLookups(header) : null,
    source,
    limit: limit ?? null,
  });
}

/** True when the server has something to look up; otherwise the form alone renders the preview. */
export function hasResolvable(body: PreviewBlockBody): boolean {
  const itemHas = (item: PreviewBannerItem | null | undefined) =>
    !!item && (!!item.media || !!item.mobileMedia || (item.ctas ?? []).length > 0);
  if (body.kind === 'banner') return body.banner.items.some(itemHas);
  const { header, source } = body.rail;
  return itemHas(header) || source.type === 'collection' || source.productIds.length > 0;
}

/** A payload together with the body that produced it, so renderers can tell stale positions. */
export interface BlockPreviewResult {
  block: StorefrontContentBlock;
  sent: PreviewBlockBody;
}

export interface UseBlockPreviewOptions {
  /** Scope of the block being edited: null for a shared block of the organisation. */
  storeId: string | null;
  /** Store the preview resolves in; null while a shared block's store list is loading. */
  previewStoreId: string | null;
  /** Market by country; undefined for the store's default market. */
  country?: string;
}

/**
 * Live preview of the block editor's form. Reads the whole form with useWatch, keys the query
 * on the resolvable fields only, debounces changes by 400 ms and keeps the previous payload on
 * screen while the next one loads or when it fails. Disabled while nothing is resolvable.
 */
export function useBlockPreview({ storeId, previewStoreId, country }: UseBlockPreviewOptions) {
  const form = useFormContext<BlockFormInput>();
  // The form is seeded with complete values, so the watched object is never partial.
  const values = useWatch({ control: form.control }) as BlockFormInput;
  const body = toPreviewBody(values);
  const key = resolvableKey(body);
  const settledKey = useDebouncedValue(key, PREVIEW_DEBOUNCE_MS);
  const enabled = previewStoreId !== null && hasResolvable(body);
  const params = {
    storeId: previewStoreId ?? '',
    ...(country ? { country } : {}),
  };

  const query = useQuery({
    queryKey: contentKeys.blockPreview(storeId, params, settledKey),
    // Built at fetch time so the body carries the latest copy too, not the copy at settle time.
    queryFn: async (): Promise<BlockPreviewResult> => {
      const sent = toPreviewBody(form.getValues());
      const block = await blocksApi.preview(storeId, sent, params);
      return { block, sent };
    },
    enabled,
    placeholderData: keepPreviousData,
    staleTime: PREVIEW_STALE_MS,
  });

  // A failed refetch drops `data`; the last good payload stays on screen under the error.
  const [lastGood, setLastGood] = useState<BlockPreviewResult | undefined>(query.data);
  if (query.data && query.data !== lastGood) setLastGood(query.data);
  const result = enabled ? (query.data ?? lastGood) : undefined;

  return {
    values,
    enabled,
    block: result?.block,
    sent: result?.sent,
    /** Waiting on the debounce or on the network. */
    isUpdating: enabled && (key !== settledKey || query.isFetching),
    error: enabled && query.isError ? query.error : null,
  };
}

export type BlockPreviewState = ReturnType<typeof useBlockPreview>;
